/**
 * @file 胡牌牌型识别（Win Detection）。
 *
 * 算法规格见 `design/gdd/03-win-detection.md`。所有函数以 **27 维计数向量**
 * 为输入，是纯函数、平台无关。本文件不引用 DOM / Node / Workers 全局。
 */

import type { Suit, Meld } from './types';
import { suitToIndex } from './tiles';

/** 缺门校验（只看手牌，副露不可能含缺门牌由 QC-2~5 保证）。 */
export function passQueCheckHand(counts: Uint8Array, queSuit: Suit): boolean {
  const base = suitToIndex(queSuit) * 9;
  for (let r = 0; r < 9; r++) {
    if ((counts[base + r] ?? 0) > 0) return false;
  }
  return true;
}

/** 某计数向量是否含缺门花色牌。 */
export function hasQueTileCounts(counts: Uint8Array, queSuit: Suit): boolean {
  return !passQueCheckHand(counts, queSuit);
}

/** 七对判定：27 种张数全为偶数（count[k]===4 自动计 2 对，即龙七对）。 */
export function isSevenPairs(counts: Uint8Array): boolean {
  for (let k = 0; k < 27; k++) {
    if ((counts[k] ?? 0) % 2 !== 0) return false;
  }
  return true;
}

/**
 * 是否**存在**一种全部由刻子构成的拆解（对对胡判定）。
 * 注意与「某一种拆解是否全刻子」不同——这里只关心存在性。
 */
export function hasAllTripletsDecomp(counts: Uint8Array): boolean {
  let pairFound = false;
  for (let k = 0; k < 27; k++) {
    const c = counts[k] ?? 0;
    const r = c % 3;
    if (r === 0) continue;
    if (r === 2 && !pairFound && c === 2) {
      pairFound = true;
      continue;
    }
    return false;
  }
  return pairFound;
}

/**
 * 面子分解（核心回溯）。尝试把 `counts` 中剩余牌全部分解为 `need` 个面子。
 *
 * @param start 搜索起点（单调不减，避免重复枚举同一组合的不同顺序）
 */
export function decompose(counts: Uint8Array, start: number, need: number): boolean {
  if (need === 0) {
    for (let k = 0; k < 27; k++) if ((counts[k] ?? 0) !== 0) return false;
    return true;
  }
  let i = start;
  while (i < 27 && (counts[i] ?? 0) === 0) i++;
  if (i === 27) return false;

  // 分支 A：以 i 组刻子
  if ((counts[i] ?? 0) >= 3) {
    counts[i] = (counts[i] ?? 0) - 3;
    if (decompose(counts, i, need - 1)) {
      counts[i] = (counts[i] ?? 0) + 3;
      return true;
    }
    counts[i] = (counts[i] ?? 0) + 3;
  }

  // 分支 B：以 i 为起点组顺子 —— 必须做花色边界检查 (i % 9) <= 6
  if ((i % 9) <= 6 && (counts[i + 1] ?? 0) > 0 && (counts[i + 2] ?? 0) > 0) {
    counts[i] = (counts[i] ?? 0) - 1;
    counts[i + 1] = (counts[i + 1] ?? 0) - 1;
    counts[i + 2] = (counts[i + 2] ?? 0) - 1;
    if (decompose(counts, i, need - 1)) {
      counts[i] = (counts[i] ?? 0) + 1;
      counts[i + 1] = (counts[i + 1] ?? 0) + 1;
      counts[i + 2] = (counts[i + 2] ?? 0) + 1;
      return true;
    }
    counts[i] = (counts[i] ?? 0) + 1;
    counts[i + 1] = (counts[i + 1] ?? 0) + 1;
    counts[i + 2] = (counts[i + 2] ?? 0) + 1;
  }

  return false;
}

/** 标准型判定（4 面子 + 1 将）。枚举将牌位置后回溯。 */
export function checkStandard(counts: Uint8Array, meldCount: number): boolean {
  const need = 4 - meldCount;
  for (let k = 0; k < 27; k++) {
    if ((counts[k] ?? 0) >= 2) {
      counts[k] = (counts[k] ?? 0) - 2;
      const ok = decompose(counts, 0, need);
      counts[k] = (counts[k] ?? 0) + 2;
      if (ok) return true;
    }
  }
  return false;
}

/**
 * 胡牌判定（以计数向量为输入）。
 *
 * @param counts    手牌计数向量（已含即将胡的那张牌）
 * @param meldCount 已副露面子数（碰 / 杠各 1）
 * @param queSuit   定缺花色；`null` 表示不限制（仅 checkWin 在缺门未声明时使用）
 */
export function canWinCounts(counts: Uint8Array, meldCount: number, queSuit: Suit | null): boolean {
  if (queSuit !== null && !passQueCheckHand(counts, queSuit)) return false;
  const total = (() => {
    let t = 0;
    for (let k = 0; k < 27; k++) t += counts[k] ?? 0;
    return t;
  })();
  if (total !== 3 * (4 - meldCount) + 2) return false;

  if (meldCount === 0 && total === 14 && isSevenPairs(counts)) return true;

  return checkStandard(counts, meldCount);
}

/** 听牌张（tileKey 列表）。只看牌型、不看剩余张数（听绝张也算）。 */
export function getWaits(counts: Uint8Array, meldCount: number, queSuit: Suit | null): number[] {
  const waits: number[] = [];
  for (let k = 0; k < 27; k++) {
    if ((counts[k] ?? 0) >= 4) continue;
    if (queSuit !== null && Math.floor(k / 9) === suitToIndex(queSuit)) continue;
    counts[k] = (counts[k] ?? 0) + 1;
    if (canWinCounts(counts, meldCount, queSuit)) waits.push(k);
    counts[k] = (counts[k] ?? 0) - 1;
  }
  return waits;
}

/** 是否听牌。 */
export function isTingCounts(counts: Uint8Array, meldCount: number, queSuit: Suit | null): boolean {
  return getWaits(counts, meldCount, queSuit).length > 0;
}

/** 副露中是否含某花色牌（防御性校验用，正常流程下恒为 false）。 */
export function meldsContainSuit(melds: readonly Meld[], queSuit: Suit): boolean {
  const q = suitToIndex(queSuit);
  for (const m of melds) {
    const suit = (m.kind.slice(0, -1) as Suit);
    if (suitToIndex(suit) === q) return true;
  }
  return false;
}
